<template>
  <div class="scroll" @click.stop @mousedown.stop>
    <div class="scroll-holder" ref="scrollHolder">
      <!-- One tick per excluded frame, aligned to where the thumb sits at that
           index, so exclusions are visible without scrolling to them. -->
      <div
        v-for="index in excludedIndices"
        :key="index"
        class="excluded-mark"
        :style="{ top: `${markOffset(index)}px` }"
        :title="`Image ${index + 1} excluded`"
      ></div>
      <input
        class="image-slider"
        type="range"
        min="0"
        :max="max"
        step="1"
        :value="value"
        @change="handleChange"
        @input="handleChange"
        :style="{ width: `${viewportHeight}px` }"
        ref="scrollThumb"
      />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, onMounted, ref, watch } from 'vue';

const scrollHolder = ref<HTMLElement | null>(null);
const scrollThumb = ref<HTMLElement | null>(null);

const props = withDefaults(
  defineProps<{
    value: number;
    imageIds: string[];
    viewportHeight: number;
    /** Stack indices to mark in the gutter as excluded from export. */
    excludedIndices?: number[];
  }>(),
  { excludedIndices: () => [] }
);

/** Kept in sync with the CSS thumb width so the marks line up with the thumb. */
const thumbSize = ref(39);

const emits = defineEmits(['update:value']);
const max = computed(() => {
  return props.imageIds.length - 1;
});

watch(max, () => {
  adjustScrollThumbSize();
});
watch(
  () => props.viewportHeight,
  () => {
    adjustScrollThumbSize();
  }
);

onMounted(() => {
  adjustScrollThumbSize();
});

function adjustScrollThumbSize() {
  if (!scrollHolder.value || !scrollThumb.value) return;
  let size = Math.floor(scrollHolder.value.clientHeight / (max.value + 1));
  if (size < 40) size = 40;
  thumbSize.value = size;
  scrollThumb.value.style.setProperty('--thumb-width', size + 'px');
  // document.querySelector('.image-slider').style.setProperty('--thumb-width', size + 'px');
}

/**
 * Where the thumb's centre sits for a given index: the thumb travels the track
 * inset by half its own width at each end, so a plain index/max fraction across
 * the full height would drift by up to 20px against the thumb.
 */
function markOffset(index: number) {
  const travel = Math.max(props.viewportHeight - thumbSize.value, 0);
  const fraction = max.value > 0 ? index / max.value : 0;
  return thumbSize.value / 2 + fraction * travel;
}

function handleChange(event: Event) {
  const intValue = parseInt((event.target as HTMLInputElement).value, 10);
  emits('update:value', intValue);
}
</script>

<style scoped>
.scroll {
  height: 100%;
  /* padding: 5px; */
  position: absolute;
  right: 0;
  top: 0;
}
.scroll .scroll-holder {
  /* height: calc(100% - 10px); */
  height: 100%;
  /* margin-top: 5px; */
  position: relative;
  width: 12px;
}
.scroll .scroll-holder .excluded-mark {
  position: absolute;
  right: 0;
  width: 12px;
  height: 3px;
  transform: translateY(-50%);
  background-color: var(--color-error);
  border-radius: 1px;
  pointer-events: none;
  z-index: 5;
}
.scroll .scroll-holder .image-slider {
  height: 12px;
  left: 12px;
  padding: 0;
  position: absolute;
  top: 0;
  transform: rotate(90deg);
  transform-origin: top left;
  -webkit-appearance: none;
  background-color: rgba(0, 0, 0, 0);
  --thumb-width: 39px;
}
.scroll .scroll-holder .image-slider:focus {
  outline: none;
}
.scroll .scroll-holder .image-slider::-moz-focus-outer {
  border: none;
}
.scroll .scroll-holder .image-slider::-webkit-slider-runnable-track {
  background-color: rgba(0, 0, 0, 0);
  border: none;
  cursor: pointer;
  height: 5px;
  z-index: 6;
}
.scroll .scroll-holder .image-slider::-moz-range-track {
  background-color: rgba(0, 0, 0, 0);
  border: none;
  cursor: pointer;
  height: 2px;
  z-index: 6;
}
.scroll .scroll-holder .image-slider::-ms-track {
  animate: 0.2s;
  background: transparent;
  border: none;
  border-width: 15px 0;
  color: rgba(0, 0, 0, 0);
  cursor: pointer;
  height: 12px;
  width: 100%;
}
.scroll .scroll-holder .image-slider::-ms-fill-lower {
  background: rgba(0, 0, 0, 0);
}
.scroll .scroll-holder .image-slider::-ms-fill-upper {
  background: rgba(0, 0, 0, 0);
}
.scroll .scroll-holder .image-slider::-webkit-slider-thumb {
  -webkit-appearance: none !important;
  /* background-color: rgba(22, 50, 57, 0.25); */
  /* border: 1px solid #ccc; */
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 5px;
  cursor: grab;
  height: 12px;
  margin-top: -4px;
  width: 39px;
  width: var(--thumb-width);
}
.scroll .scroll-holder .image-slider::-webkit-slider-thumb:active {
  /* background-color: rgba(22, 50, 57, 0.60); */
  background-color: rgba(255, 255, 255, 1);
  cursor: grabbing;
}
.scroll .scroll-holder .image-slider::-moz-range-thumb {
  /* background-color: rgba(22, 50, 57, 0.25); */
  border: none;
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 5px;
  cursor: grab;
  height: 12px;
  width: 39px;
  width: var(--thumb-width);
  z-index: 7;
}
.scroll .scroll-holder .image-slider::-moz-range-thumb:active {
  /* background-color: rgba(22, 50, 57, 0.60); */
  background-color: rgba(255, 255, 255, 1);
  cursor: grabbing;
}
.scroll .scroll-holder .image-slider::-ms-thumb {
  /* background-color: rgba(22, 50, 57, 0.25); */
  /* border: 1px solid #ccc; */
  background-color: rgba(255, 255, 255, 0.6);
  border-radius: 5px;
  cursor: ns-resize;
  height: 12px;
  width: 39px;
  width: var(--thumb-width);
}
.scroll .scroll-holder .image-slider::-ms-thumb:active {
  /* background-color: rgba(22, 50, 57, 0.60); */
  background-color: rgba(255, 255, 255, 1);
}
.scroll .scroll-holder .image-slider::-ms-tooltip {
  display: none;
}
@media screen and (-ms-high-contrast: active), (-ms-high-contrast: none) {
  .image-slider {
    left: 50px;
  }
}
</style>
